import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, tap, of, map } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  SinequaQueryRequest,
  SinequaQueryResponse,
} from '../models/sinequa.model';
import { faro } from '@grafana/faro-web-sdk';
import { AuthService } from './auth.service';


@Injectable({
  providedIn: 'root',
})
export class SinequaService {
  // Secure proxy endpoint (API Gateway → Lambda → SSM). The Lambda injects the
  // Sinequa token + CF-Access headers server-side.
  private readonly apiUrl = environment.aws.sinequaEndpoint;
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _lastQuery = signal<string | null>(null);

  readonly loading = computed(() => this._loading());
  readonly error = computed(() => this._error());
  readonly lastQuery = computed(() => this._lastQuery());

  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  search(request: SinequaQueryRequest, trackLoading = true): Observable<any> {
    if (trackLoading) {
      this._loading.set(true);
      this._error.set(null);
      this._lastQuery.set(request.query);
    }

    // Sinequa POST payload structure
    const faroSessionId = faro?.api?.getSession?.()?.id ?? 'faro-session-unavailable';
    // Read the claim from AuthService rather than off Faro's meta bag. The
    // previous version looked up an attribute name the auth code had stopped
    // writing, and an untyped string-keyed lookup cannot fail loudly — every
    // audit row recorded `guest-unknown` instead.
    const pingSub = this.auth.sub() ?? 'guest-unknown';
    const decodedUrl = decodeURIComponent(window.location.href);


    const payload = {
      app: 'panorama.platform.search',
      query: {
        name: 'panorama.platform.query',
        text: request.query,
        page: request.page ?? 1,
        pageSize: request.pageSize ?? 10,
      },
      "$auditRecord": {
        "auditEvents": [
          {
            "type": "Search_Text",
            "detail": {
              "querytext": request.query,
              sessionid: faroSessionId,
              url: decodedUrl,
              ping_sub: pingSub,
            }
          }
        ]
      },

      'ui-language': 'en',
    };

    // Add optional parameters if provided
    if (request.sort) {
      (payload.query as any).sort = request.sort;
    }

    if (request.filters && request.filters.length > 0) {
      // Group filters by field so same-field selections are OR'd together
      // (e.g. "category=A OR category=B") and different fields are AND'd
      // (e.g. "category=A AND offeringType=X").
      const groupedByField = new Map<string, string[]>();
      const expressionFilters: string[] = [];

      for (const f of request.filters) {
        const value = Array.isArray(f.value) ? f.value.join(',') : f.value;
        // Pre-built expressions (like size ranges) are passed through unchanged
        if (value.includes(':(')) {
          expressionFilters.push(value);
          continue;
        }
        const expr = `${f.field}:\`${value}\``;
        const list = groupedByField.get(f.field) ?? [];
        list.push(expr);
        groupedByField.set(f.field, list);
      }

      const groupExpressions: string[] = [];
      for (const expressions of groupedByField.values()) {
        groupExpressions.push(
          expressions.length === 1
            ? expressions[0]
            : `(${expressions.join(' OR ')})`,
        );
      }
      groupExpressions.push(...expressionFilters);

      (payload.query as any).select = groupExpressions.join(' AND ');
    }

    if (request.columns && request.columns.length > 0) {
      (payload.query as any).columns = request.columns;
    }


    return this.http.post<any>(`${this.apiUrl}/query`, payload).pipe(
      tap((response) => {
        if (trackLoading) {
          this._loading.set(false);
        }
      }),
      catchError((error) => {
        if (trackLoading) {
          this._loading.set(false);
          this._error.set(error.message || 'An error occurred while searching');
        }
        console.error('Sinequa search error:', error);
        return of({
          error: true,
          message: error.message,
          totalCount: 0,
          page: request.page ?? 1,
          pageSize: request.pageSize ?? 10,
          results: [],
        });
      }),
    );
  }

  simpleSearch(query: string, page = 1, pageSize = 10): Observable<any> {
    return this.search({ query, page, pageSize });
  }

  autocomplete(query: string): Observable<string[]> {
    if (!query || query.trim().length < 2) {
      return of([]);
    }

    const payload = {
      app: 'panorama.platform.search',
      suggestQuery: 'panorama.suggestion_query',
      text: query,
      query: 'panorama.platform.query',
      'ui-language': 'en',
    };


    return this.http.post<any>(`${this.apiUrl}/suggestquery`, payload).pipe(
      tap((response) => {
      }),
      map((response) => {
        if (response && response.Suggests && Array.isArray(response.Suggests)) {
          return response.Suggests.map(
            (s: any) => s.Display || s.Normalized || s.display || s.text || s,
          );
        }

        return [];
      }),
      catchError((error) => {
        console.error('Sinequa autocomplete error:', error);
        return of([]);
      }),
    );
  }

  clearError(): void {
    this._error.set(null);
  }
}
